/**
 * Card Registry — single source of truth for all configurable cards across
 * Dashboard, Analytics, and Asset Analytics pages.
 *
 * Each entry defines:
 * - key: unique identifier stored in template configs
 * - label: i18n key for display name
 * - page: which page(s) this card belongs to
 * - defaultVisible: whether shown by default when no template exists
 * - defaultOrder: render order when no template exists
 * - allowedChartTypes: which display modes this card supports
 * - defaultChartType: the default display mode
 * - settingsKey: the legacy SettingsContext boolean key (if any)
 */

// Chart type options
export const CHART_TYPES = {
  LINE: "line",
  STRAIGHT_LINE: "straight_line",
  DOTTED_LINE: "dotted_line",
  BAR: "bar",
  PIE: "pie",
  BOLD_NUMBER: "bold_number",
  TABLE: "table",
};

// Chart type labels for the UI menu
export const CHART_TYPE_LABELS = {
  [CHART_TYPES.LINE]: "cardRegistry.chartTypes.line",
  [CHART_TYPES.STRAIGHT_LINE]: "cardRegistry.chartTypes.straightLine",
  [CHART_TYPES.DOTTED_LINE]: "cardRegistry.chartTypes.dottedLine",
  [CHART_TYPES.BAR]: "cardRegistry.chartTypes.bar",
  [CHART_TYPES.PIE]: "cardRegistry.chartTypes.pie",
  [CHART_TYPES.BOLD_NUMBER]: "cardRegistry.chartTypes.boldNumber",
  [CHART_TYPES.TABLE]: "cardRegistry.chartTypes.table",
};

// Page identifiers
export const PAGES = {
  DASHBOARD: "dashboard",
  ANALYTICS: "analytics",
  ASSET_ANALYTICS: "asset_analytics",
};

// ── Dashboard Cards ──────────────────────────────────────────────────
export const DASHBOARD_CARDS = [
  {
    key: "impressions_card",
    label: "cardRegistry.dashboard.impressionsCard",
    defaultVisible: true,
    defaultOrder: 1,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showTotalImpressionsCard",
  },
  {
    key: "sales_card",
    label: "cardRegistry.dashboard.salesCard",
    defaultVisible: true,
    defaultOrder: 2,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showTotalSalesCard",
  },
  {
    key: "revenue_card",
    label: "cardRegistry.dashboard.revenueCard",
    defaultVisible: true,
    defaultOrder: 3,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showTotalRevenueCard",
  },
  {
    key: "linked_sources",
    label: "cardRegistry.dashboard.linkedSources",
    defaultVisible: true,
    defaultOrder: 4,
    allowedChartTypes: [],
    defaultChartType: null,
    settingsKey: null,
  },
  {
    key: "assets_list",
    label: "cardRegistry.dashboard.assetsList",
    defaultVisible: true,
    defaultOrder: 5,
    allowedChartTypes: [],
    defaultChartType: null,
    settingsKey: null,
  },
];

// ── Analytics Cards ──────────────────────────────────────────────────
export const ANALYTICS_CARDS = [
  {
    key: "sales_over_time",
    label: "cardRegistry.analytics.salesOverTime",
    defaultVisible: true,
    defaultOrder: 1,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showSalesOverTime",
  },
  {
    key: "rentals_over_time",
    label: "cardRegistry.analytics.rentalsOverTime",
    defaultVisible: true,
    defaultOrder: 2,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showRentalsOverTime",
  },
  {
    key: "impressions_over_time",
    label: "cardRegistry.analytics.impressionsOverTime",
    defaultVisible: true,
    defaultOrder: 3,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showImpressionsOverTime",
  },
  {
    key: "impression_revenue_over_time",
    label: "cardRegistry.analytics.impressionRevenueOverTime",
    defaultVisible: true,
    defaultOrder: 4,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showImpressionRevenueOverTime",
  },
  {
    key: "total_earnings_over_time",
    label: "cardRegistry.analytics.totalEarningsOverTime",
    defaultVisible: true,
    defaultOrder: 5,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showTotalEarningsOverTime",
  },
  {
    key: "roi_over_time",
    label: "cardRegistry.analytics.roiOverTime",
    defaultVisible: true,
    defaultOrder: 6,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showROIOverTime",
  },
  {
    key: "sales_stats_card",
    label: "cardRegistry.analytics.salesStatsCard",
    defaultVisible: true,
    defaultOrder: 7,
    allowedChartTypes: [CHART_TYPES.BOLD_NUMBER, CHART_TYPES.BAR],
    defaultChartType: CHART_TYPES.BOLD_NUMBER,
    settingsKey: null,
  },
  {
    key: "general_stats_card",
    label: "cardRegistry.analytics.generalStatsCard",
    defaultVisible: true,
    defaultOrder: 8,
    allowedChartTypes: [CHART_TYPES.BOLD_NUMBER, CHART_TYPES.BAR],
    defaultChartType: CHART_TYPES.BOLD_NUMBER,
    settingsKey: null,
  },
  {
    key: "earnings_card",
    label: "cardRegistry.analytics.earningsCard",
    defaultVisible: true,
    defaultOrder: 9,
    allowedChartTypes: [CHART_TYPES.BOLD_NUMBER],
    defaultChartType: CHART_TYPES.BOLD_NUMBER,
    settingsKey: null,
  },
  {
    key: "detailed_stats_cards",
    label: "cardRegistry.analytics.detailedStatsCards",
    defaultVisible: true,
    defaultOrder: 10,
    allowedChartTypes: [
      CHART_TYPES.BAR,
      CHART_TYPES.PIE,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.BAR,
    settingsKey: "showDetailedBreakdown",
  },
  {
    key: "source_analytics",
    label: "cardRegistry.analytics.sourceAnalytics",
    defaultVisible: true,
    defaultOrder: 11,
    allowedChartTypes: [
      CHART_TYPES.TABLE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.TABLE,
    settingsKey: null,
  },
  {
    key: "platform_sales_details",
    label: "cardRegistry.analytics.platformSalesDetails",
    defaultVisible: true,
    defaultOrder: 12,
    allowedChartTypes: [
      CHART_TYPES.TABLE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.TABLE,
    settingsKey: "showSalesDetails",
  },
  {
    key: "top_by_impressions",
    label: "cardRegistry.analytics.topByImpressions",
    defaultVisible: true,
    defaultOrder: 13,
    allowedChartTypes: [
      CHART_TYPES.TABLE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.TABLE,
    settingsKey: null,
  },
  {
    key: "top_by_sales",
    label: "cardRegistry.analytics.topBySales",
    defaultVisible: true,
    defaultOrder: 14,
    allowedChartTypes: [
      CHART_TYPES.TABLE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.TABLE,
    settingsKey: null,
  },
];

// ── Asset Analytics Cards ────────────────────────────────────────────
export const ASSET_ANALYTICS_CARDS = [
  {
    key: "sales_over_time",
    label: "cardRegistry.analytics.salesOverTime",
    defaultVisible: true,
    defaultOrder: 1,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showSalesOverTime",
  },
  {
    key: "rentals_over_time",
    label: "cardRegistry.analytics.rentalsOverTime",
    defaultVisible: true,
    defaultOrder: 2,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showRentalsOverTime",
  },
  {
    key: "impressions_over_time",
    label: "cardRegistry.analytics.impressionsOverTime",
    defaultVisible: true,
    defaultOrder: 3,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showImpressionsOverTime",
  },
  {
    key: "impression_revenue_over_time",
    label: "cardRegistry.analytics.impressionRevenueOverTime",
    defaultVisible: true,
    defaultOrder: 4,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showImpressionRevenueOverTime",
  },
  {
    key: "total_earnings_over_time",
    label: "cardRegistry.analytics.totalEarningsOverTime",
    defaultVisible: true,
    defaultOrder: 5,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showTotalEarningsOverTime",
  },
  {
    key: "roi_over_time",
    label: "cardRegistry.analytics.roiOverTime",
    defaultVisible: true,
    defaultOrder: 6,
    allowedChartTypes: [
      CHART_TYPES.LINE,
      CHART_TYPES.STRAIGHT_LINE,
      CHART_TYPES.DOTTED_LINE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.LINE,
    settingsKey: "showROIOverTime",
  },
  {
    key: "sales_stats_card",
    label: "cardRegistry.analytics.salesStatsCard",
    defaultVisible: true,
    defaultOrder: 7,
    allowedChartTypes: [CHART_TYPES.BOLD_NUMBER, CHART_TYPES.BAR],
    defaultChartType: CHART_TYPES.BOLD_NUMBER,
    settingsKey: null,
  },
  {
    key: "general_stats_card",
    label: "cardRegistry.analytics.generalStatsCard",
    defaultVisible: true,
    defaultOrder: 8,
    allowedChartTypes: [CHART_TYPES.BOLD_NUMBER, CHART_TYPES.BAR],
    defaultChartType: CHART_TYPES.BOLD_NUMBER,
    settingsKey: null,
  },
  {
    key: "earnings_card",
    label: "cardRegistry.analytics.earningsCard",
    defaultVisible: true,
    defaultOrder: 9,
    allowedChartTypes: [CHART_TYPES.BOLD_NUMBER],
    defaultChartType: CHART_TYPES.BOLD_NUMBER,
    settingsKey: null,
  },
  {
    key: "detailed_stats_cards",
    label: "cardRegistry.analytics.detailedStatsCards",
    defaultVisible: true,
    defaultOrder: 10,
    allowedChartTypes: [
      CHART_TYPES.BAR,
      CHART_TYPES.PIE,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.BAR,
    settingsKey: "showDetailedBreakdown",
  },
  {
    key: "sales_details",
    label: "cardRegistry.assetAnalytics.salesDetails",
    defaultVisible: true,
    defaultOrder: 11,
    allowedChartTypes: [
      CHART_TYPES.TABLE,
      CHART_TYPES.BAR,
      CHART_TYPES.BOLD_NUMBER,
    ],
    defaultChartType: CHART_TYPES.TABLE,
    settingsKey: "showSalesDetails",
  },
];

/**
 * Build the default card config array from a registry.
 * Used when no template config exists for a page.
 */
export function buildDefaultConfig(registry) {
  return registry.map((card) => ({
    key: card.key,
    visible: card.defaultVisible,
    order: card.defaultOrder,
    chart_type: card.defaultChartType,
  }));
}

/**
 * Merge a saved template config with the registry to produce a resolved config.
 * - Cards in the template config keep their saved order/visibility/chart_type.
 * - Cards in the registry but missing from the config are appended (new cards).
 * - Cards in the config but removed from the registry are dropped (deleted cards).
 */
export function resolveCardConfig(savedConfig, registry) {
  if (!savedConfig || savedConfig.length === 0) {
    return buildDefaultConfig(registry);
  }

  const registryKeys = new Set(registry.map((c) => c.key));
  const registryMap = Object.fromEntries(registry.map((c) => [c.key, c]));

  // Keep only cards that still exist in the registry
  const resolved = savedConfig
    .filter((item) => registryKeys.has(item.key))
    .map((item) => ({
      key: item.key,
      visible: item.visible ?? registryMap[item.key].defaultVisible,
      order: item.order,
      chart_type: item.chart_type ?? registryMap[item.key].defaultChartType,
    }));

  // Append any new registry cards not in saved config
  const savedKeys = new Set(savedConfig.map((c) => c.key));
  const maxOrder =
    resolved.length > 0 ? Math.max(...resolved.map((c) => c.order)) : 0;
  let nextOrder = maxOrder + 1;

  for (const card of registry) {
    if (!savedKeys.has(card.key)) {
      resolved.push({
        key: card.key,
        visible: card.defaultVisible,
        order: nextOrder++,
        chart_type: card.defaultChartType,
      });
    }
  }

  return resolved.sort((a, b) => a.order - b.order);
}
